import {
	Button,
	LinearProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import React, { useContext, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import { ErrorTypography, TextFieldStack } from "../../styled/formComponents";
import { UserApi } from "../../../api/userApi";
import { findComponentStep } from "../../../utils/filter";
import {
	BusinessDataContext,
	BusinessDataContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import { emptyUser } from "../../../types/entities";
import { tryCatchHandler } from "../../../utils/tryCatchHandler";

interface RecoverValues {
	token_0: string;
	token_1: string;
	token_2: string;
	token_3: string;
	token_4: string;
	token_5: string;
}

export const TokenForm = ({ email, setSuccess }) => {
	const { formatMessage } = useIntl();
	const { onChangeStep, onChangeLogin } = useContext(
		MultistepContext
	) as MultistepContextValues;
	const { selectedFlow } = useContext(
		BusinessDataContext
	) as BusinessDataContextValues;

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [submitted, setSubmitted] = useState(false);

	// Crear las referencias fuera de la función de renderizado
	const refs = useRef(
		Array.from({ length: 6 }, () => React.createRef<HTMLInputElement>())
	);

	const initialValues = {
		token_0: "",
		token_1: "",
		token_2: "",
		token_3: "",
		token_4: "",
		token_5: "",
	};

	const recoverSchema = Yup.object().shape({
		token_0: Yup.string().trim().required(),
		token_1: Yup.string().trim().required(),
		token_2: Yup.string().trim().required(),
		token_3: Yup.string().trim().required(),
		token_4: Yup.string().trim().required(),
		token_5: Yup.string().trim().required(),
	});

	const handleRecoverPassword = async (values: RecoverValues) => {
		setError("");
		setLoading(true);
		const userApi = new UserApi();

		const errorMessage = await tryCatchHandler(
			async () => {
				const token =
					values.token_0 +
					values.token_1 +
					values.token_2 +
					values.token_3 +
					values.token_4 +
					values.token_5;
				await userApi.validateToken(email, token);
				const customer = { ...emptyUser, email };
				onChangeLogin(customer);
				const changePasswordStep = findComponentStep(
					selectedFlow!,
					"ChangePassword"
				);
				onChangeStep(changePasswordStep);
				setSuccess(
					formatMessage({
						id: "recoverPassword.tokenSuccess",
						defaultMessage: "¡Token válido!",
					})
				);
				setLoading(false);
			},
			"recoverPassword.tokenError",
			"Ocurrió un error al verificar el token.",
			formatMessage
		);

		if (errorMessage) setError(errorMessage);
		setLoading(false);
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={recoverSchema}
			onSubmit={async (values) => {
				setSubmitted(true);
				await handleRecoverPassword(values);
			}}
		>
			{({
				values,
				errors,
				touched,
				handleChange,
				handleBlur,
				handleSubmit,
				isSubmitting,
				setFieldValue,
			}) => (
				<Form style={{ width: "100%", alignItems: "center" }}>
					<Stack spacing={2}>
						<TextFieldStack spacing={1}>
							<Typography
								color="primary"
								fontFamily="Noto Sans Gujarati"
								fontWeight={700}
							>
								<FormattedMessage id="field.token" defaultMessage={"Token"} />
								<span> *</span>
							</Typography>

							<Stack direction="row" spacing={1}>
								{Array.from({ length: 6 }, (_, index) => (
									<TextField
										key={index}
										name={`token_${index}`}
										value={values[`token_${index}`]}
										type="text"
										inputProps={{ maxLength: 1 }}
										error={Boolean(
											submitted &&
												touched[`token_${index}`] &&
												errors[`token_${index}`]
										)}
										onBlur={handleBlur}
										inputRef={refs.current[index]} // Asigna la referencia
										onChange={(event) => {
											handleChange(event);
											if (event.target.value.length === 1 && index < 5) {
												refs.current[index + 1]?.current?.focus(); // Mueve el foco al siguiente campo
											}
										}}
										onKeyDown={(event) => {
											const target = event.target as HTMLInputElement;
											if (event.key === "Enter") {
												handleSubmit(); // Envía el formulario al presionar Enter
											} else if (
												event.key === "Backspace" &&
												target.value === ""
											) {
												// Si el valor está vacío y se presiona Backspace
												if (index > 0) {
													refs.current[index - 1]?.current?.focus(); // Mueve el foco al campo anterior
													setTimeout(() => {
														// Borra el valor del campo anterior
														handleChange({
															target: {
																name: `token_${index - 1}`,
																value: "",
															},
														});
													}, 0);
												}
											}
										}}
										// Maneja el evento paste en el primer input
										onPaste={(event) => {
											if (index === 0) {
												event.preventDefault(); // Previene el comportamiento por defecto de pegar
												const clipboardData =
													event.clipboardData.getData("Text"); // Obtén el contenido del portapapeles
												const tokens = clipboardData.split("").slice(0, 6); // Divide el contenido en caracteres y usa máximo 6
												tokens.forEach((char, i) => {
													setFieldValue(`token_${i}`, char); // Establece el valor en el campo correspondiente
													if (i < 5) {
														refs.current[i + 1]?.current?.focus(); // Mueve el foco al siguiente campo
													}
												});
											}
										}}
										sx={{ "& input": { textAlign: "center" } }}
									/>
								))}
							</Stack>
						</TextFieldStack>

						{error && <ErrorTypography>{error}</ErrorTypography>}

						{loading && <LinearProgress />}
						<Button
							type="submit"
							fullWidth
							variant="contained"
							disabled={isSubmitting}
							sx={{ height: "50px" }}
							onClick={(event) => {
								event.preventDefault();
								handleSubmit();
							}}
						>
							<FormattedMessage
								id="recoverPassword.tokenTitle"
								defaultMessage="Verificar token"
							/>
						</Button>
					</Stack>
				</Form>
			)}
		</Formik>
	);
};
